/**
 * Completing a sale when the network may not be there.
 *
 * The order of operations is the whole point, and it is deliberately the
 * pessimistic one:
 *
 *   1. write the sale to IndexedDB
 *   2. enqueue the upload
 *   3. *then* try the network
 *
 * Not the other way round. A till that posted first and stored on failure would
 * lose the sale to a browser crash between the two, and would have no record at
 * all of a request that timed out but actually succeeded. Storing first means
 * the worst case is an upload that has to be retried — never a sale that
 * happened and left no trace.
 *
 * The receipt number is allocated locally, from this device's own sequence. That
 * is what lets a cashier hand over a printed receipt with no signal. The server
 * allocates its own on arrival; the local one is what the customer holds, and
 * both are recorded.
 */

import { ApiError, NetworkError } from "@/api/client";
import { db, getSyncMeta, updateSyncMeta } from "@/db/database";
import type { LocalPayment, LocalSale, LocalSaleLine } from "@/db/schema";
import { authRepository } from "@/repositories/authRepository";
import { localStockRepository } from "@/repositories/localStockRepository";
import { outboxRepository } from "@/repositories/outboxRepository";
import { salesRepository, type PaymentMethod, type Sale } from "@/repositories/salesRepository";

export interface LocalSaleInput {
  saleId: string;
  idempotencyKey: string;
  tillId: string;
  receiptPrefix: string;
  lines: {
    productId: string;
    name: string;
    quantity: number;
    unitPrice: string;
    taxRate: string;
    /**
     * Chosen extras, as **ids**. The server looks up what each costs, exactly as
     * it looks up the product's price — so a queued sale that uploads tomorrow
     * is priced by the server then, not by whatever the till believed today.
     */
    modifierIds?: string[];
  }[];
  method: PaymentMethod;
  tendered: number;
  total: number;
  /** The account holder, for a sale on account. */
  customerId?: string;
  /** The till's counter: where the stock comes off, here and on the server. */
  sectionId?: string;
}

/** A sale on account was attempted with no signal. Nothing was recorded. */
export class AccountSaleNeedsSignal extends Error {
  constructor() {
    super(
      "A sale on account needs signal, so the till can check what they still owe. Take another payment, or try again when there is signal.",
    );
    this.name = "AccountSaleNeedsSignal";
  }
}

export interface CompletionOutcome {
  /** What to show the cashier. Always present — a sale always produces a receipt. */
  sale: Sale;
  /** True when the sale is on this device only, waiting to upload. */
  queued: boolean;
}

export const localSaleRepository = {
  /**
   * Ring up a sale, online or not.
   *
   * Returns a receipt either way. The cashier's job is to serve the person in
   * front of them; whether the server has heard about it yet is the software's
   * problem, and the sync centre's.
   */
  async complete(input: LocalSaleInput): Promise<CompletionOutcome> {
    if (input.method === "ACCOUNT") return this.completeOnAccount(input);

    const businessDate = new Date().toISOString().slice(0, 10);
    const receiptNo = await nextReceiptNumber(input.receiptPrefix);

    const cashierId = await authRepository.cashierId();
    const payload = {
      // Identical on both routes, so the idempotency fingerprint matches.
      ...(cashierId ? { cashier_id: cashierId } : {}),
      sale_id: input.saleId,
      till_id: input.tillId,
      items: input.lines.map((line) => ({
        product_id: line.productId,
        quantity: String(line.quantity),
        ...(line.modifierIds?.length ? { modifier_ids: line.modifierIds } : {}),
      })),
      payments: [
        {
          method: input.method,
          amount: input.total.toFixed(2),
          tendered: input.tendered.toFixed(2),
        },
      ],
      client_created_at: new Date().toISOString(),
    };

    const local: LocalSale = {
      id: input.saleId,
      receiptNo,
      businessDate,
      clientCreatedAt: payload.client_created_at,
      subtotal: input.total.toFixed(2),
      discountTotal: "0.00",
      taxTotal: "0.00",
      total: input.total.toFixed(2),
      lines: input.lines.map<LocalSaleLine>((line, index) => ({
        id: `${input.saleId}-${index}`,
        productId: line.productId,
        productNameSnapshot: line.name,
        quantity: String(line.quantity),
        unitPrice: line.unitPrice,
        discountAmount: "0.00",
        taxRate: line.taxRate,
        taxAmount: "0.00",
        lineTotal: (Number(line.unitPrice) * line.quantity).toFixed(2),
      })),
      // What it was paid with, so the till knows offline which of its own
      // sales it may give cash back on.
      payments: [
        {
          id: `${input.saleId}-p1`,
          saleId: input.saleId,
          method: input.method as LocalPayment["method"],
          amount: input.total.toFixed(2),
          tendered: input.tendered.toFixed(2),
          change: null,
          syncState: "PENDING",
        },
      ],
      customerId: null,
      ...(input.sectionId ? { sectionId: input.sectionId } : {}),
      syncState: "PENDING",
      acknowledgedAt: null,
    };

    // Steps 1 and 2, in one transaction. A sale that was stored but never queued
    // would sit on the device forever looking complete.
    let operationId = "";
    // The stock change is in the same transaction. It used to follow it, so a
    // power cut between the two left a sale on the till whose items were still
    // on the local shelf count until the next pull.
    await db.transaction(
      "rw",
      [db.pendingSales, db.outbox, db.syncMeta, db.stockLevels],
      async () => {
        await db.pendingSales.put(local);
        const operation = await outboxRepository.enqueue({
          type: "sale.create",
          entityId: input.saleId,
          idempotencyKey: input.idempotencyKey,
          payload,
          businessDate,
        });
        operationId = operation.opId;
        await this.adjustLocalStock(input);
      },
    );

    // Step 3. Failure here is expected and unremarkable — the operation is
    // already queued, so there is nothing to recover.
    try {
      const sale = await salesRepository.complete(payload, input.idempotencyKey);
      await db.pendingSales.update(input.saleId, { syncState: "SYNCED" });
      // Take it out of the queue. Leaving it there — which is what this did at
      // first — meant the engine replayed every sale a minute later, the server
      // answered "duplicate", and the sale was acknowledged after two requests
      // instead of one. Correct, and twice the data bundle on a prepaid SIM.
      await outboxRepository.acknowledge(operationId);
      return { sale, queued: false };
    } catch (error) {
      if (error instanceof NetworkError || (error instanceof ApiError && error.isRetryable)) {
        return { sale: offlineReceipt(local, input), queued: true };
      }

      // A 4xx means the request is wrong, not that the network is. The engine
      // will surface it as a conflict; the cashier still gets a receipt, because
      // the customer has already paid and walked away.
      return { sale: offlineReceipt(local, input), queued: true };
    }
  },

  /**
   * A sale on account: online, and only online.
   *
   * Never stored or queued first. The credit limit is the server's to check,
   * against a balance other tills are also spending, and a queued account sale
   * would be credit given on a figure that may already be gone (§4.8). So the
   * server is asked directly, and with no answer nothing has happened: the
   * cashier is told, the basket is still there, and the customer pays another
   * way or waits.
   */
  async completeOnAccount(input: LocalSaleInput): Promise<CompletionOutcome> {
    if (!input.customerId) throw new Error("Choose who the sale is for.");

    const cashierId = await authRepository.cashierId();
    const payload = {
      ...(cashierId ? { cashier_id: cashierId } : {}),
      sale_id: input.saleId,
      till_id: input.tillId,
      customer_id: input.customerId,
      items: input.lines.map((line) => ({
        product_id: line.productId,
        quantity: String(line.quantity),
        ...(line.modifierIds?.length ? { modifier_ids: line.modifierIds } : {}),
      })),
      payments: [{ method: "ACCOUNT" as const, amount: input.total.toFixed(2) }],
      client_created_at: new Date().toISOString(),
    };

    try {
      const sale = await salesRepository.complete(payload, input.idempotencyKey);
      await this.adjustLocalStock(input);
      return { sale, queued: false };
    } catch (error) {
      if (error instanceof NetworkError || (error instanceof ApiError && error.isRetryable)) {
        throw new AccountSaleNeedsSignal();
      }
      throw error;
    }
  },

  /**
   * Move the local stock figure so the grid reflects what has just been sold.
   *
   * Written to `pendingDelta`, never to `serverQty`. The next pull overwrites
   * the server figure and leaves this one alone, so a sale made offline stays
   * subtracted until the server confirms it and sends back a balance that
   * already includes it.
   */
  async adjustLocalStock(input: LocalSaleInput): Promise<void> {
    // From the till's own counter, which is the one the server moves. See
    // `localStockRepository.move` for a till that does not know it.
    await db.transaction("rw", db.stockLevels, async () => {
      for (const line of input.lines) {
        await localStockRepository.move(input.sectionId ?? null, line.productId, -line.quantity);
      }
    });
  },

  /** Sales made on this device that the server has not acknowledged. */
  unsynced: () => db.pendingSales.where("syncState").notEqual("SYNCED").toArray(),
};

/**
 * The next receipt number from this device's own sequence.
 *
 * Prefixed with the till code, which is why the format is till-scoped: a
 * merchant-wide sequence cannot be advanced by an offline terminal without
 * colliding with every other terminal doing the same thing.
 */
async function nextReceiptNumber(prefix: string): Promise<string> {
  return db.transaction("rw", db.syncMeta, async () => {
    // `getSyncMeta` creates the row if this is a fresh install. Reading it
    // directly and guarding the write on its existence — which is what this did
    // first — meant the sequence never advanced at all, so two offline sales in
    // a row both printed T01-000001. Two customers holding receipts with the
    // same number is not a cosmetic problem.
    const meta = await getSyncMeta();
    const sequence = meta.receiptSequence;
    await updateSyncMeta({ receiptSequence: sequence + 1 });
    return `${prefix}-${String(sequence).padStart(6, "0")}`;
  });
}

/**
 * A receipt for a sale the server has not seen.
 *
 * Shaped exactly like the server's, so the receipt dialog needs no idea whether
 * the sale uploaded. The one visible difference is the receipt number, which is
 * this device's — and that is honest, because it is the number printed on the
 * paper in the customer's hand.
 */
function offlineReceipt(local: LocalSale, input: LocalSaleInput): Sale {
  return {
    id: local.id,
    receipt_number: local.receiptNo,
    status: "COMPLETED",
    business_date: local.businessDate,
    subtotal: local.subtotal,
    discount_total: "0.00",
    tax_total: "0.00",
    rounding_adjustment: "0.00",
    total: local.total,
    amount_paid: input.tendered.toFixed(2),
    change_given: Math.max(input.tendered - input.total, 0).toFixed(2),
    vat_mode: "NONE",
    vat_rate: "0.00",
    note: "",
    completed_at: local.clientCreatedAt,
    created_at: local.clientCreatedAt,
    cashier_name: "",
    till_name: "",
    item_count: local.lines.length,
    lines: local.lines.map((line) => ({
      id: line.id,
      line_number: 1,
      product: line.productId,
      description: line.productNameSnapshot,
      unit_code: "ea",
      quantity: line.quantity,
      unit_price: line.unitPrice,
      discount_amount: "0.00",
      tax_rate: line.taxRate,
      tax_amount: "0.00",
      // Empty on an offline receipt: the extras are already folded into the
      // line's description and price, and there is nothing here that needs to
      // read them back. The server's copy carries the structured list.
      modifiers: [],
      net_amount: line.lineTotal,
      line_total: line.lineTotal,
    })),
    payments: [],
  };
}
